import { API_BASE_URL } from '../config/api';

export interface InvoiceStats {
  totalInvoices: number;
  totalAmount: number;
  collectedAmount: number;
  receivedAmount: number;
  pendingAmount: number;
  invoicesByStatus: {
    collected: number;
    received: number;
    pending: number;
  };
}

export interface OrderStats {
  totalOrders: number;
  totalShipForMe: number;
  ordersByStatus: {
    [key: string]: number;
  };
  totalRevenue: number;
  averageOrderValue: number;
}

export interface DashboardData {
  invoiceStats: InvoiceStats;
  orderStats: OrderStats;
  recentInvoices: any[];
  recentOrders: any[];
}

class DashboardService {
  private baseUrl = API_BASE_URL;

  async getInvoiceStats(): Promise<InvoiceStats> {
    try {
      const response = await fetch(`${this.baseUrl}/Invoice?page=1&pageSize=10000`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch invoices');

      const data = await response.json();
      const invoices = data.items || [];

      const stats: InvoiceStats = {
        totalInvoices: data.totalItems || data.totalCount || invoices.length,
        totalAmount: invoices.reduce((sum: number, inv: any) => sum + (inv.amount || 0), 0),
        collectedAmount: invoices
          .filter((inv: any) => inv.isCollected)
          .reduce((sum: number, inv: any) => sum + (inv.amount || 0), 0),
        receivedAmount: invoices
          .filter((inv: any) => inv.isReceived)
          .reduce((sum: number, inv: any) => sum + (inv.amount || 0), 0),
        pendingAmount: invoices
          .filter((inv: any) => !inv.isCollected && !inv.isReceived)
          .reduce((sum: number, inv: any) => sum + (inv.amount || 0), 0),
        invoicesByStatus: {
          collected: invoices.filter((inv: any) => inv.isCollected).length,
          received: invoices.filter((inv: any) => inv.isReceived).length,
          pending: invoices.filter((inv: any) => !inv.isCollected && !inv.isReceived).length,
        },
      };

      return stats;
    } catch (error) {
      console.error('Error fetching invoice stats:', error);
      throw error;
    }
  }

  async getOrderStats(): Promise<OrderStats> {
    try {
      const token = localStorage.getItem('token');

      // Fetch both BuyForMe (AdminOrder) and ShipForMe (AdminShipForMe) orders
      const [buyForMeResponse, shipForMeResponse] = await Promise.all([
        fetch(`${this.baseUrl}/AdminOrder?page=1&pageSize=10000`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }),
        fetch(`${this.baseUrl}/AdminShipForMe?page=1&pageSize=10000`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }),
      ]);

      if (!buyForMeResponse.ok) throw new Error('Failed to fetch BuyForMe orders');
      if (!shipForMeResponse.ok) throw new Error('Failed to fetch ShipForMe orders');

      const buyForMeData = await buyForMeResponse.json();
      const shipForMeData = await shipForMeResponse.json();

      console.log('BuyForMe Response:', buyForMeData);
      console.log('ShipForMe Response:', shipForMeData);

      const buyForMeOrders = buyForMeData.items || [];
      const shipForMeOrders = shipForMeData.items || [];
      const allOrders = [...buyForMeOrders, ...shipForMeOrders];

      console.log('BuyForMe Orders:', buyForMeOrders);
      console.log('ShipForMe Orders:', shipForMeOrders);

      // Calculate revenue from both order types
      const buyForMeRevenue = buyForMeOrders.reduce((sum: number, o: any) => sum + (o.totalPrice || 0), 0);
      const shipForMeRevenue = shipForMeOrders.reduce((sum: number, o: any) => sum + (o.totalPrice || 0), 0);
      const totalRevenue = buyForMeRevenue + shipForMeRevenue;

      const stats: OrderStats = {
        totalOrders: buyForMeData.totalItems || buyForMeData.totalCount || buyForMeOrders.length || 0,
        totalShipForMe: shipForMeData.totalItems || shipForMeData.totalCount || shipForMeOrders.length || 0,
        ordersByStatus: this.groupByStatus(allOrders),
        totalRevenue: totalRevenue,
        averageOrderValue: allOrders.length > 0 ? totalRevenue / allOrders.length : 0,
      };

      console.log('Order Stats:', stats);
      return stats;
    } catch (error) {
      console.error('Error fetching order stats:', error);
      throw error;
    }
  }

  private groupByStatus(orders: any[]): { [key: string]: number } {
    const statusMap: { [key: string]: number } = {};
    orders.forEach((order: any) => {
      const status = order.orderStatus || 'Unknown';
      statusMap[status] = (statusMap[status] || 0) + 1;
    });
    return statusMap;
  }

  async getDashboardData(): Promise<DashboardData> {
    try {
      const [invoiceStats, orderStats] = await Promise.all([
        this.getInvoiceStats(),
        this.getOrderStats(),
      ]);

      return {
        invoiceStats,
        orderStats,
        recentInvoices: [],
        recentOrders: [],
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }
}

export const dashboardService = new DashboardService();

